import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { DismissOption, ViewType } from '../../../constants/parameters';
import { mapAccountPinErrorCodeToResource } from '../../../constants/resources';
import { ModalFragmentProps } from '../../../constants/types';
import useAccountSecurityPromptContext from '../../../hooks/useAccountSecurityPromptContext';
import { AccountSecurityPromptActionType } from '../../../store/action';
import ModalState from '../../../store/modalState';
import { dismissTemporary, startDismissForever } from '../../commonHandlers';
import { FooterButtonConfig, FragmentModalFooter } from '../fragments/modalFooter';
import { FragmentModalHeader, HeaderButtonType } from '../fragments/modalHeader';

/**
 * The start of the change password modal flow.
 */
const ModalChangePasswordIntro: React.FC<ModalFragmentProps> = ({
  closeModal
}: ModalFragmentProps) => {
  const {
    state: { promptType, viewType, dismissOption, resources, requestService },
    dispatch
  } = useAccountSecurityPromptContext();

  /*
   * Component State
   */

  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestInFlight, setRequestInFlight] = useState<boolean>(false);

  /*
   * Event Handlers
   */

  const continueToChangePassword = async () => {
    setRequestError(null);
    setRequestInFlight(true);
    const result = await requestService.getAccountPinState();
    setRequestInFlight(false);
    if (result.isError) {
      setRequestError(mapAccountPinErrorCodeToResource(resources, result.error));
      return;
    }

    const accountPinUnlocked =
      result.value.unlockedUntil !== null && result.value.unlockedUntil > 0;
    if (!result.value.isEnabled || accountPinUnlocked) {
      // Transition: Set the time the pin is unlocked for, then show the change
      // password page.
      dispatch({
        type: AccountSecurityPromptActionType.SET_ACCOUNT_PIN_UNLOCKED_UNTIL,
        accountPinUnlockedUntil:
          result.value.unlockedUntil !== null
            ? Date.now() + result.value.unlockedUntil * 1000
            : null
      });
      dispatch({
        type: AccountSecurityPromptActionType.SET_MODAL_STATE,
        modalState: ModalState.CHANGE_PASSWORD_FORM
      });
      return;
    }

    // Transition: Account pin needs to be unlocked.
    dispatch({
      type: AccountSecurityPromptActionType.SET_MODAL_STATE,
      modalState: ModalState.ACCOUNT_PIN_FORM
    });
  };

  /*
   * Render Properties
   */

  const positiveButton: FooterButtonConfig = {
    // Show a spinner as the button content when a request is in flight.
    content: requestInFlight ? (
      <span className='spinner spinner-xs spinner-no-margin' />
    ) : (
      resources.Action.ContinueChangePassword
    ),
    label: resources.Action.ContinueChangePassword,
    enabled: !requestInFlight,
    action: continueToChangePassword
  };

  const negativeButton: FooterButtonConfig | null =
    viewType === ViewType.BANNER || dismissOption === DismissOption.TEMPORARY_ONLY
      ? null
      : {
          content: resources.Action.DismissForever,
          label: resources.Action.DismissForever,
          enabled: !requestInFlight,
          action: startDismissForever(dispatch, promptType)
        };

  /*
   * Component Markup
   */

  return (
    <React.Fragment>
      <FragmentModalHeader
        headerText={resources.Header.ChangeYourPassword}
        buttonType={HeaderButtonType.CLOSE}
        buttonAction={viewType === ViewType.BANNER ? closeModal : dismissTemporary(dispatch)}
        buttonEnabled={!requestInFlight}
        headerInfo={null}
      />
      <Modal.Body>
        <div className='modal-shield' />
        <p className='modal-margin-bottom modal-text-danger'>
          {resources.Description.UnusualActivity}
        </p>
        <p>{resources.Description.ChangeYourPassword}</p>
        <p className='text-error xsmall'>{requestError}</p>
      </Modal.Body>
      <FragmentModalFooter positiveButton={positiveButton} negativeButton={negativeButton} />
    </React.Fragment>
  );
};

export default ModalChangePasswordIntro;
